import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { formatIssueTypeName, ISSUE_DESCRIPTIONS } from '../../utils/issueFormatting';

interface CriticalIssue {
  id: string;
  name: string;
  description: string;
  severity: 'critical' | 'high';
  flowCount: number;
  estimatedHours?: number;
  flows: {
    id: string;
    name: string;
    orgId: string;
    performanceScore: number;
  }[];
}

interface CriticalIssuesWidgetProps {
  criticalIssues: any[];
  selectedOrgId: string | null;
  forceExpanded?: boolean;
}

// Estimate time based on issue type and flow count

// Estimate time based on issue type and flow count
const estimateTime = (issueType: string, flowCount: number): number => {
  const baseTimePerFlow: Record<string, number> = {
    hardcoded_ids: 0.5, // 30 minutes per flow
    missing_error_handling: 0.75, // 45 minutes per flow
    api_limits: 1.0, // 1 hour per flow
    recursive_loops: 1.5, // 1.5 hours per flow
    performance_critical: 2.0, // 2 hours per flow
    no_entry_criteria: 0.25, // 15 minutes per flow
    complex_nested_loops: 1.0 // 1 hour per flow
  };
  
  return (baseTimePerFlow[issueType] || 0.5) * flowCount;
};

export default function CriticalIssuesWidget({ criticalIssues: rawCriticalIssues, selectedOrgId, forceExpanded = false }: CriticalIssuesWidgetProps) {
  const navigate = useNavigate();
  const [criticalIssuesExpanded, setCriticalIssuesExpanded] = useState(forceExpanded);
  const [expandedIssues, setExpandedIssues] = useState<Set<string>>(new Set());
  const [criticalIssues, setCriticalIssues] = useState<CriticalIssue[]>([]);

  // Set expansion state when forceExpanded changes
  useEffect(() => {
    if (forceExpanded) {
      setCriticalIssuesExpanded(true);
    }
  }, [forceExpanded]);

  // Group critical issues by type
  useEffect(() => {
    const issuesMap = new Map<string, CriticalIssue>();

    // Group critical issues by issue type
    rawCriticalIssues.forEach(issue => {
      // Map issue types based on the issue message/type
      let issueType = 'performance_critical'; // default
      const message = (issue.issue || '').toLowerCase();
      const type = (issue.issueType || '').toLowerCase();
      
      // Map issue types from API to display names
      if (type === 'hardcoded_salesforce_ids' || type === 'hardcoded_ids' || message.includes('hardcoded') || message.includes('hard-coded')) {
        issueType = 'hardcoded_ids';
      } else if (type === 'dml_operations_in_loops' || type === 'dml_in_loops' || message.includes('dml in loop')) {
        issueType = 'dml_in_loops';
      } else if (type === 'queries_in_loops' || message.includes('queries in loop') || message.includes('soql in loop')) {
        issueType = 'queries_in_loops';
      } else if (type === 'not_bulkified' || type === 'no_bulkification' || message.includes('bulkif')) {
        issueType = 'not_bulkified';
      } else if (type === 'non_indexed_query' || message.includes('non-indexed') || message.includes('non indexed')) {
        issueType = 'non_indexed_query';
      } else if (type === 'unfiltered_query' || message.includes('unfiltered')) {
        issueType = 'unfiltered_query';
      } else if (type === 'performance_issues' || message.includes('performance')) {
        issueType = 'performance_issues';
      } else if (type === 'missing_error_handling' || message.includes('error handling')) {
        issueType = 'missing_error_handling';
      } else {
        // Use the original type in lowercase
        issueType = type.toLowerCase();
      }

      if (!issuesMap.has(issueType)) {
        // Use the formatIssueTypeName function to get consistent naming
        const originalType = issue.issueType.toUpperCase();
        const displayName = formatIssueTypeName(originalType);
        const description = ISSUE_DESCRIPTIONS[originalType] || issue.issue || 'Performance or quality issue';
        
        issuesMap.set(issueType, {
          id: issueType,
          name: displayName,
          description: description,
          severity: issue.severity as 'critical' | 'high', // Use severity from API, not hardcoded
          flowCount: 0,
          flows: []
        });
      }
      
      const issueGroup = issuesMap.get(issueType)!;
      issueGroup.flows.push({
        id: issue.flowId,
        name: issue.flowName,
        orgId: selectedOrgId || '',
        performanceScore: issue.performanceScore
      });
      issueGroup.flowCount = issueGroup.flows.length;
      issueGroup.estimatedHours = estimateTime(issueType, issueGroup.flowCount);
    });

    // Only show HIGH severity issues (mapped to 'high' in the frontend)
    const sortedIssues = Array.from(issuesMap.values())
      .filter(issue => issue.severity === 'high' || issue.severity === 'critical') // Show high severity issues
      .sort((a, b) => b.flowCount - a.flowCount);

    setCriticalIssues(sortedIssues);
  }, [rawCriticalIssues, selectedOrgId]);

  const toggleIssue = (issueId: string) => {
    const newExpanded = new Set(expandedIssues);
    if (newExpanded.has(issueId)) {
      newExpanded.delete(issueId);
    } else {
      newExpanded.add(issueId);
    }
    setExpandedIssues(newExpanded);
  };

  const totalIssues = criticalIssues.length;
  const totalPoints = criticalIssues.reduce((sum, issue) => sum + (issue.flowCount * (issue.severity === 'critical' ? 10 : 5)), 0);
  const totalHours = criticalIssues.reduce((sum, issue) => sum + (issue.estimatedHours || 0), 0);

  const formatTime = (hours: number): string => {
    if (hours < 1) {
      return `${Math.round(hours * 60)}m`;
    }
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  };

  return (
    <div id="critical-issues" className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <ExclamationTriangleIcon className="w-5 h-5 mr-2 text-red-500" />
              Critical Issues
            </h3>
            {!criticalIssuesExpanded && criticalIssues.length > 0 && (
              <p className="text-sm text-gray-500 mt-1">
                {totalIssues} issues • {totalPoints} points • ~{formatTime(totalHours)}
              </p>
            )}
          </div>
          <button
            onClick={() => setCriticalIssuesExpanded(!criticalIssuesExpanded)}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg className={`w-5 h-5 transform transition-transform ${criticalIssuesExpanded ? '' : 'rotate-180'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
      </div>
      {criticalIssuesExpanded && (
        <div className="p-6">
          {criticalIssues.length > 0 ? (
            <div className="space-y-3">
              {criticalIssues.map((issue) => (
                <div key={issue.id} className="border border-gray-200 rounded-lg">
                  <button
                    onClick={() => toggleIssue(issue.id)}
                    className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center flex-1">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mr-3 ${
                        issue.severity === 'critical' 
                          ? 'bg-red-100 text-red-800' 
                          : 'bg-orange-100 text-orange-800'
                      }`}>
                        {issue.severity}
                      </span>
                      <div className="text-left">
                        <h4 className="text-sm font-medium text-gray-900">{issue.name}</h4>
                        <p className="text-xs text-gray-500 mt-0.5">{issue.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center ml-4">
                      <span className="text-sm text-gray-500 mr-3">
                        {issue.flowCount} flows{issue.estimatedHours && ` • ~${formatTime(issue.estimatedHours)}`}
                      </span>
                      <svg 
                        className={`w-5 h-5 text-gray-400 transform transition-transform ${
                          expandedIssues.has(issue.id) ? '' : 'rotate-180'
                        }`} 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </button>
                  {expandedIssues.has(issue.id) && (
                    <div className="border-t bg-gray-50 px-4 py-2">
                      <div className="space-y-2">
                        {issue.flows.map((flow, index) => (
                          <div
                            key={`${flow.id}-${index}`}
                            className="flex items-center justify-between py-2 hover:bg-gray-100 px-2 rounded cursor-pointer"
                            onClick={() => navigate(`/${flow.orgId}/flow/${flow.id}`)}
                          >
                            <div className="flex-1">
                              <span className="text-sm font-medium text-gray-700">
                                {flow.name}
                              </span>
                              <div className="text-xs text-gray-600 mt-1">
                                Performance Score: {flow.performanceScore}%
                                <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                  flow.performanceScore >= 80 ? 'bg-green-100 text-green-800' :
                                  flow.performanceScore >= 60 ? 'bg-yellow-100 text-yellow-800' :
                                  flow.performanceScore >= 40 ? 'bg-orange-100 text-orange-800' :
                                  'bg-red-100 text-red-800'
                                }`}>
                                  {flow.performanceScore >= 80 ? 'Good' :
                                   flow.performanceScore >= 60 ? 'Fair' :
                                   flow.performanceScore >= 40 ? 'Poor' :
                                   'Critical'}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center text-blue-600 hover:text-blue-700">
                              <span className="text-sm">View Flow</span>
                              <span className="ml-1">→</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 text-center py-4">
              No critical issues found - great job!
            </p>
          )}
        </div>
      )}
    </div>
  );
}